/**
 * Content Packs Management Page
 *
 * Admin interface for managing content packs (lucky charm themes, backgrounds, etc.)
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Package, Plus, Upload, Trash2, Edit, Image, FolderInput, X, Save, ChevronDown, ChevronUp, Settings2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ColorInput } from '@/components/ui/ColorInput'
import { FontColorInput } from '@/components/ui/FontColorInput'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Modal } from '@/components/common/Modal'
import {
  useContentPackList,
  useContentPack,
  useCreateContentPack,
  useUpdateContentPack,
  useDeleteContentPack,
  useUploadItems,
  useDeleteItem,
  useUpdateItemMetadata,
  useMigrateFilesystem,
} from '@/features/content-packs/hooks/useContentPacks'
import { contentPacksApi } from '@/lib/api/contentPacks'
import type { ContentPack, ContentPackItem, ContentPackCreate, ContentPackUpdate, ItemRenderMetadata, PackMetadata, OverlayConfig, ColorInfo, TextAreaInfo } from '@/lib/api/contentPacks'
import clsx from 'clsx'

// ============================================================================
// Status Badge
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-600',
  }
  const labels: Record<string, string> = {
    draft: '草稿',
    active: '已激活',
    archived: '已归档',
  }
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.draft)}>
      {labels[status] || status}
    </span>
  )
}

function TypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    lucky_charm_theme: '签文主题',
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {labels[type] || type}
    </span>
  )
}

// ============================================================================
// Create Pack Dialog
// ============================================================================

function CreatePackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createMutation = useCreateContentPack()
  const [form, setForm] = useState<ContentPackCreate>({
    type: 'lucky_charm_theme',
    name: '',
    display_name: '',
    description: '',
    status: 'draft',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.display_name) return
    await createMutation.mutateAsync(form)
    setForm({ type: 'lucky_charm_theme', name: '', display_name: '', description: '', status: 'draft' })
    onClose()
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="创建内容包" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="类型"
          value={form.type}
          onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
          options={[
            { value: 'lucky_charm_theme', label: '签文主题' },
          ]}
        />
        <Input
          label="标识名"
          placeholder="如 classic_ink"
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          required
          helperText="唯一标识，创建后不可更改"
        />
        <Input
          label="显示名称"
          placeholder="如 经典水墨"
          value={form.display_name}
          onChange={(e) => setForm(prev => ({ ...prev, display_name: e.target.value }))}
          required
        />
        <Textarea
          label="描述"
          placeholder="内容包描述..."
          value={form.description || ''}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
        <Select
          label="状态"
          value={form.status || 'draft'}
          onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
          options={[
            { value: 'draft', label: '草稿' },
            { value: 'active', label: '激活' },
          ]}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>取消</Button>
          <Button type="submit" loading={createMutation.isPending}>
            <Plus className="w-4 h-4 mr-1" />
            创建
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ============================================================================
// Metadata Helpers
// ============================================================================

function hasCustomMetadata(metadata: ItemRenderMetadata | undefined): boolean {
  if (!metadata) return false
  return !!(metadata.view_port || metadata.text_box || metadata.font_color ||
    metadata.outline_color || metadata.rotation_angle !== undefined ||
    metadata.font_scale_x !== undefined || metadata.line_spacing !== undefined ||
    metadata.text_align !== undefined || metadata.text_effect !== undefined ||
    metadata.shadow_blur !== undefined || metadata.shadow_opacity !== undefined ||
    metadata.shadow_color !== undefined ||
    metadata.display_name || metadata.description)
}

const PREVIEW_PHRASES: { label: string; text: string }[] = [
  { label: '短', text: '吉祥如意' },
  { label: '中', text: '幸运符文字预览' },
  { label: '长', text: '愿你在每一个清晨都能感受到生活的温暖与美好' },
]

const BUCKET_LABELS: Record<string, string> = { light: '亮色背景', mid: '中间色背景', dark: '暗色背景', invalid: '无效区域' }
const MODE_LABELS: Record<string, string> = { auto: '自动', fixed: '固定', palette: '调色板' }

function ServerRenderedPreview({
  packId,
  itemId,
  meta,
  items,
}: {
  packId: string
  itemId?: string
  meta: ItemRenderMetadata
  items?: ContentPackItem[]
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [colorInfo, setColorInfo] = useState<ColorInfo | null>(null)
  const [textAreaInfo, setTextAreaInfo] = useState<TextAreaInfo | null>(null)
  const [showTextArea, setShowTextArea] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFullscreen, setShowFullscreen] = useState(false)
  const [phraseIdx, setPhraseIdx] = useState(1)
  const [selectedBgId, setSelectedBgId] = useState<string | undefined>(itemId)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevKeyRef = useRef<string>('')

  // Filter to background items only (exclude overlays)
  const bgItems = items?.filter(i => i.item_type === 'background') ?? []

  const fetchPreview = useCallback(async (metadata: ItemRenderMetadata, phrase: string, bgId?: string) => {
    setLoading(true)
    setError(null)
    try {
      const result = bgId
        ? await contentPacksApi.previewItem(packId, bgId, metadata, phrase)
        : await contentPacksApi.previewPack(packId, metadata, phrase)
      const url = URL.createObjectURL(result.blob)
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
      setColorInfo(result.colorInfo)
      setTextAreaInfo(result.textArea)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || '预览失败')
    } finally {
      setLoading(false)
    }
  }, [packId])

  // Debounced fetch on metadata change, immediate on phrase/bg switch
  const prevMetaRef = useRef<string>(JSON.stringify(meta))
  useEffect(() => {
    const key = JSON.stringify({ meta, phraseIdx, selectedBgId })
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    const metaJson = JSON.stringify(meta)
    const metaChanged = metaJson !== prevMetaRef.current
    prevMetaRef.current = metaJson
    const delay = metaChanged ? 600 : 0

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(
      () => fetchPreview(meta, PREVIEW_PHRASES[phraseIdx].text, selectedBgId),
      delay,
    )
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [meta, phraseIdx, selectedBgId, fetchPreview])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-100 border-b border-gray-200 text-xs flex-wrap">
          {/* Text length switcher */}
          <span className="text-gray-500 shrink-0">文字:</span>
          <div className="flex gap-0.5">
            {PREVIEW_PHRASES.map((p, i) => (
              <button
                key={i}
                type="button"
                className={clsx(
                  'px-1.5 py-0.5 rounded text-xs',
                  i === phraseIdx ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
                )}
                onClick={() => setPhraseIdx(i)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Background image switcher */}
          {bgItems.length > 1 && (
            <>
              <span className="text-gray-300 mx-1">|</span>
              <span className="text-gray-500 shrink-0">底图:</span>
              <select
                className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white max-w-[120px] truncate"
                value={selectedBgId ?? ''}
                onChange={e => setSelectedBgId(e.target.value || undefined)}
              >
                {!itemId && <option value="">随机</option>}
                {bgItems.map(bg => (
                  <option key={bg.id} value={bg.id}>
                    {bg.filename.replace(/\.[^.]+$/, '')}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Text area overlay toggle */}
          <span className="text-gray-300 mx-1">|</span>
          <label className="flex items-center gap-1 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={showTextArea}
              onChange={e => setShowTextArea(e.target.checked)}
              className="w-3 h-3 rounded border-gray-300 text-blue-600"
            />
            <span className="text-gray-500">文字区域</span>
          </label>

          <div className="flex-1" />
          {loading && <span className="text-gray-400">渲染中...</span>}
        </div>

        {/* Preview image with optional text area overlay */}
        {previewUrl ? (
          <div className="flex justify-center bg-gray-100">
            <div
              className="relative cursor-pointer group"
              style={textAreaInfo && textAreaInfo.imgW > 0
                ? { aspectRatio: `${textAreaInfo.imgW}/${textAreaInfo.imgH}`, maxHeight: 240 }
                : { maxHeight: 240 }}
              onClick={() => setShowFullscreen(true)}
            >
              <img src={previewUrl} alt="渲染预览" className="h-full w-full object-fill" style={{ maxHeight: 240 }} />
              {/* Text area overlay rectangle */}
              {showTextArea && textAreaInfo && textAreaInfo.wPct > 0 && (
                <div
                  className="absolute border-2 border-dashed border-red-500/70 bg-red-500/10 pointer-events-none"
                  style={{
                    left: `${textAreaInfo.xPct}%`,
                    top: `${textAreaInfo.yPct}%`,
                    width: `${textAreaInfo.wPct}%`,
                    height: `${textAreaInfo.hPct}%`,
                  }}
                >
                  <span className="absolute -top-4 left-0 text-[10px] text-red-600 bg-white/80 px-1 rounded whitespace-nowrap">
                    {textAreaInfo.wPx}x{textAreaInfo.hPx} @ ({textAreaInfo.xPx},{textAreaInfo.yPx})
                  </span>
                </div>
              )}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <span className="text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">渲染中...</span>
                </div>
              )}
              {!showTextArea && !loading && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">
                    点击放大
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="min-h-[80px] flex items-center justify-center">
            {loading && <span className="text-xs text-gray-400">渲染中...</span>}
            {error && !loading && <span className="text-xs text-red-400">{error}</span>}
            {!loading && !error && <span className="text-xs text-gray-400">等待预览...</span>}
          </div>
        )}

        {/* Color resolution info bar */}
        {colorInfo && !loading && (
          <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-500 flex-wrap">
            <span>颜色模式: <strong className="text-gray-700">{MODE_LABELS[colorInfo.configMode] || colorInfo.configMode}</strong></span>
            {colorInfo.luminanceBucket && (
              <>
                <span className="text-gray-300">|</span>
                <span>背景: <strong className="text-gray-700">{BUCKET_LABELS[colorInfo.luminanceBucket] || colorInfo.luminanceBucket}</strong></span>
              </>
            )}
            {colorInfo.luminance !== null && (
              <>
                <span className="text-gray-300">|</span>
                <span>亮度: {colorInfo.luminance.toFixed(2)}</span>
              </>
            )}
            {colorInfo.resolvedColor && (
              <>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1">
                  实际颜色:
                  <span
                    className="inline-block w-3 h-3 rounded border border-gray-300"
                    style={{ backgroundColor: colorInfo.resolvedColor }}
                  />
                  <strong className="text-gray-700">{colorInfo.resolvedColor}</strong>
                </span>
              </>
            )}
            {colorInfo.fallbackReason && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-amber-600">
                  fallback: {colorInfo.fallbackReason === 'no_mid_bg_auto' ? '无 mid_bg, 自动选色' : colorInfo.fallbackReason}
                </span>
              </>
            )}
            {textAreaInfo && textAreaInfo.imgW > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span>图片: {textAreaInfo.imgW}x{textAreaInfo.imgH}</span>
                <span className="text-gray-300">|</span>
                <span>文字区域: {textAreaInfo.wPx}x{textAreaInfo.hPx} @ ({textAreaInfo.xPx},{textAreaInfo.yPx})</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen lightbox */}
      {showFullscreen && previewUrl && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setShowFullscreen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl"
            onClick={() => setShowFullscreen(false)}
          >
            &times;
          </button>
          <img
            src={previewUrl}
            alt="渲染预览"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}

// ============================================================================
// Item Metadata Dialog
// ============================================================================

function ItemMetadataDialog({
  open,
  onClose,
  item,
  packId,
  items,
}: {
  open: boolean
  onClose: () => void
  item: ContentPackItem
  packId: string
  items?: ContentPackItem[]
}) {
  const updateMutation = useUpdateItemMetadata()
  const [form, setForm] = useState<ItemRenderMetadata>(() => ({ ...item.metadata }))
  const [showFullscreen, setShowFullscreen] = useState(false)

  const isInheriting = !hasCustomMetadata(form)

  const handleSave = async () => {
    await updateMutation.mutateAsync({ packId, itemId: item.id, metadata: form })
    onClose()
  }

  const handleReset = async () => {
    const empty: ItemRenderMetadata = {}
    await updateMutation.mutateAsync({ packId, itemId: item.id, metadata: empty })
    setForm({})
    onClose()
  }

  const updateField = <K extends keyof ItemRenderMetadata>(key: K, value: ItemRenderMetadata[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="项目元数据配置" size="2xl">
      <div className="space-y-5">
        {/* Image preview + header */}
        {item.thumbnail_url && (
          <div
            className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer group relative"
            onClick={() => setShowFullscreen(true)}
          >
            <img
              src={item.thumbnail_url}
              alt={item.filename}
              className="w-full max-h-80 object-contain"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-white bg-black/50 px-3 py-1 rounded-full">
                点击全屏查看
              </span>
            </div>
          </div>
        )}
        {/* Fullscreen lightbox */}
        {showFullscreen && item.thumbnail_url && (
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center cursor-pointer"
            onClick={() => setShowFullscreen(false)}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              onClick={() => setShowFullscreen(false)}
            >
              &times;
            </button>
            <img
              src={item.thumbnail_url}
              alt={item.filename}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-900">{item.filename}</p>
            <p className="text-xs text-gray-500">
              {isInheriting ? (
                <span className="text-amber-600">继承包默认 (点击字段覆盖)</span>
              ) : (
                <span className="text-green-600">自定义配置</span>
              )}
            </p>
          </div>
        </div>

        {/* Rendering fields */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">渲染参数</h4>

          {/* view_port */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">画布尺寸 (view_port)</label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="宽度"
                type="number"
                value={form.view_port?.width ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  updateField('view_port', val !== undefined
                    ? { width: val, height: form.view_port?.height ?? 0 }
                    : undefined)
                }}
              />
              <Input
                placeholder="高度"
                type="number"
                value={form.view_port?.height ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  updateField('view_port', val !== undefined
                    ? { width: form.view_port?.width ?? 0, height: val }
                    : undefined)
                }}
              />
            </div>
          </div>

          {/* text_box */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">文字区域 (text_box)</label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="top"
                type="number"
                value={form.text_box?.top ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  if (val !== undefined) {
                    updateField('text_box', { top: val, left: form.text_box?.left ?? 0, width: form.text_box?.width ?? 0, height: form.text_box?.height ?? 0 })
                  }
                }}
              />
              <Input
                placeholder="left"
                type="number"
                value={form.text_box?.left ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  if (val !== undefined) {
                    updateField('text_box', { top: form.text_box?.top ?? 0, left: val, width: form.text_box?.width ?? 0, height: form.text_box?.height ?? 0 })
                  }
                }}
              />
              <Input
                placeholder="宽度"
                type="number"
                value={form.text_box?.width ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  if (val !== undefined) {
                    updateField('text_box', { top: form.text_box?.top ?? 0, left: form.text_box?.left ?? 0, width: val, height: form.text_box?.height ?? 0 })
                  }
                }}
              />
              <Input
                placeholder="高度"
                type="number"
                value={form.text_box?.height ?? ''}
                onChange={e => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  if (val !== undefined) {
                    updateField('text_box', { top: form.text_box?.top ?? 0, left: form.text_box?.left ?? 0, width: form.text_box?.width ?? 0, height: val })
                  }
                }}
              />
            </div>
          </div>

          {/* Font color */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">字体颜色 (font_color)</label>
            <FontColorInput
              value={form.font_color}
              onChange={v => updateField('font_color', v as ItemRenderMetadata['font_color'])}
            />
          </div>

          {/* Text effect button group */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">文字效果</label>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              {(['none', 'outline', 'shadow'] as const).map(effect => (
                <button
                  key={effect}
                  type="button"
                  className={clsx(
                    'px-4 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                    (form.text_effect ?? 'none') === effect
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  )}
                  onClick={() => {
                    const updated: ItemRenderMetadata = {
                      ...form,
                      text_effect: effect === 'none' ? undefined : effect,
                    }
                    if (effect === 'none') {
                      updated.outline_color = undefined
                      updated.shadow_blur = undefined
                      updated.shadow_opacity = undefined
                      updated.shadow_color = undefined
                      updated.shadow_distance = undefined
                      updated.shadow_angle = undefined
                      updated.shadow_spread = undefined
                      updated.shadow_blend_mode = undefined
                    } else if (effect === 'outline') {
                      updated.shadow_blur = undefined
                      updated.shadow_opacity = undefined
                      updated.shadow_color = undefined
                      updated.shadow_distance = undefined
                      updated.shadow_angle = undefined
                      updated.shadow_spread = undefined
                      updated.shadow_blend_mode = undefined
                    } else {
                      updated.outline_color = undefined
                    }
                    setForm(updated)
                  }}
                >
                  {{ none: '无效果', outline: '描边', shadow: '投影' }[effect]}
                </button>
              ))}
            </div>
          </div>

          {/* Server-rendered text effect preview */}
          {form.text_effect && form.text_effect !== 'none' && (
            <ServerRenderedPreview packId={packId} itemId={item.id} meta={form} items={items} />
          )}

          {/* Outline fields */}
          {(form.text_effect === 'outline') && (
            <div className="pl-3 border-l-2 border-blue-200 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">描边颜色</label>
                <ColorInput
                  placeholder='#FFFFFF'
                  value={form.outline_color}
                  onChange={v => updateField('outline_color', v)}
                />
              </div>
            </div>
          )}

          {/* Shadow fields */}
          {(form.text_effect === 'shadow') && (
            <div className="pl-3 border-l-2 border-blue-200 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">投影颜色</label>
                  <ColorInput
                    placeholder='#444444'
                    value={form.shadow_color}
                    onChange={v => updateField('shadow_color', v)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">大小 (px)</label>
                  <Input
                    type="number" step="1" placeholder="84"
                    value={form.shadow_blur ?? ''}
                    onChange={e => updateField('shadow_blur', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">不透明度 (%)</label>
                  <Input
                    type="number" step="5" min="0" max="100" placeholder="85"
                    value={form.shadow_opacity ?? ''}
                    onChange={e => updateField('shadow_opacity', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">距离 (px)</label>
                  <Input
                    type="number" step="1" min="0" placeholder="0"
                    value={form.shadow_distance ?? ''}
                    onChange={e => updateField('shadow_distance', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">角度 (°)</label>
                  <Input
                    type="number" step="1" min="0" max="360" placeholder="120"
                    value={form.shadow_angle ?? ''}
                    onChange={e => updateField('shadow_angle', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={!form.shadow_distance}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">扩展 (%)</label>
                  <Input
                    type="number" step="5" min="0" max="100" placeholder="0"
                    value={form.shadow_spread ?? ''}
                    onChange={e => updateField('shadow_spread', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">混合模式</label>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  {(['normal', 'multiply'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={clsx(
                        'px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                        (form.shadow_blend_mode ?? 'normal') === mode
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      )}
                      onClick={() => updateField('shadow_blend_mode', mode === 'normal' ? undefined : mode)}
                    >
                      {{ normal: '正常', multiply: '正片叠底' }[mode]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Numeric fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">旋转角度</label>
              <Input
                type="number"
                step="1"
                placeholder="0"
                value={form.rotation_angle ?? ''}
                onChange={e => updateField('rotation_angle', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">字体缩放 X</label>
              <Input
                type="number"
                step="0.1"
                placeholder="1.0"
                value={form.font_scale_x ?? ''}
                onChange={e => updateField('font_scale_x', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">行间距</label>
              <Input
                type="number"
                step="0.1"
                placeholder="1.3"
                value={form.line_spacing ?? ''}
                onChange={e => updateField('line_spacing', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">文字对齐</label>
              <Select
                value={form.text_align ?? ''}
                onChange={e => updateField('text_align', (e.target.value || undefined) as 'left' | 'center' | 'right' | undefined)}
                options={[
                  { value: '', label: '默认 (居中)' },
                  { value: 'left', label: '左对齐' },
                  { value: 'center', label: '居中' },
                  { value: 'right', label: '右对齐' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Display fields */}
        <div className="space-y-4 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700">展示信息</h4>
          <Input
            label="展示名称 (display_name)"
            placeholder="如 水墨山水"
            value={form.display_name ?? ''}
            onChange={e => updateField('display_name', e.target.value || undefined)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="主题 (theme)"
              placeholder="如 山水"
              value={form.description?.theme ?? ''}
              onChange={e => updateField('description', {
                ...form.description,
                theme: e.target.value || undefined,
              })}
            />
            <Input
              label="情绪 (emotion)"
              placeholder="如 宁静"
              value={form.description?.emotion ?? ''}
              onChange={e => updateField('description', {
                ...form.description,
                emotion: e.target.value || undefined,
              })}
            />
          </div>
          <Input
            label="关键词 (keywords)"
            placeholder="逗号分隔，如: 山水,水墨,禅意"
            value={form.description?.keywords?.join(', ') ?? ''}
            onChange={e => {
              const raw = e.target.value
              const keywords = raw ? raw.split(/[,，]\s*/).filter(Boolean) : undefined
              updateField('description', {
                ...form.description,
                keywords: keywords?.length ? keywords : undefined,
              })
            }}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            loading={updateMutation.isPending}
          >
            重置为空(继承包默认)
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
            <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-1" />
              保存
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================================
// Structured Pack Metadata Editor
// ============================================================================

function PackMetadataEditor({
  metadata,
  onChange,
  packId,
  items,
}: {
  metadata: PackMetadata
  onChange: (m: PackMetadata) => void
  packId: string
  items?: ContentPackItem[]
}) {
  const [showRawJson, setShowRawJson] = useState(false)
  const [rawText, setRawText] = useState('')

  const overlay = metadata.overlay ?? { enabled: false }
  const defaults = metadata.default_metadata ?? {}

  const updateOverlay = (partial: Partial<OverlayConfig>) => {
    onChange({ ...metadata, overlay: { ...overlay, ...partial } })
  }

  const updateDefault = <K extends keyof ItemRenderMetadata>(key: K, value: ItemRenderMetadata[K]) => {
    onChange({ ...metadata, default_metadata: { ...defaults, [key]: value } })
  }

  return (
    <div className="space-y-4">
      {/* Overlay section */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">覆盖层 (overlay)</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={overlay.enabled}
                onChange={e => updateOverlay({ enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">启用</span>
            </label>
          </div>
          <Input
            label="文件名"
            placeholder="overlay.png"
            value={overlay.filename ?? ''}
            onChange={e => updateOverlay({ filename: e.target.value || undefined })}
          />
          <Select
            label="位置"
            value={overlay.position ?? 'top'}
            onChange={e => updateOverlay({ position: e.target.value })}
            options={[
              { value: 'top', label: '顶部' },
              { value: 'bottom', label: '底部' },
              { value: 'center', label: '居中' },
            ]}
          />
        </div>
      </div>

      {/* Default metadata section */}
      <div className="space-y-3 pt-3 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700">默认渲染参数 (default_metadata)</h4>

        {/* view_port */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">画布尺寸</label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="宽度"
              type="number"
              value={defaults.view_port?.width ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                updateDefault('view_port', val !== undefined
                  ? { width: val, height: defaults.view_port?.height ?? 0 }
                  : undefined)
              }}
            />
            <Input
              placeholder="高度"
              type="number"
              value={defaults.view_port?.height ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                updateDefault('view_port', val !== undefined
                  ? { width: defaults.view_port?.width ?? 0, height: val }
                  : undefined)
              }}
            />
          </div>
        </div>

        {/* text_box */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">文字区域</label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="top"
              type="number"
              value={defaults.text_box?.top ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                if (val !== undefined) {
                  updateDefault('text_box', { top: val, left: defaults.text_box?.left ?? 0, width: defaults.text_box?.width ?? 0, height: defaults.text_box?.height ?? 0 })
                }
              }}
            />
            <Input
              placeholder="left"
              type="number"
              value={defaults.text_box?.left ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                if (val !== undefined) {
                  updateDefault('text_box', { top: defaults.text_box?.top ?? 0, left: val, width: defaults.text_box?.width ?? 0, height: defaults.text_box?.height ?? 0 })
                }
              }}
            />
            <Input
              placeholder="宽度"
              type="number"
              value={defaults.text_box?.width ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                if (val !== undefined) {
                  updateDefault('text_box', { top: defaults.text_box?.top ?? 0, left: defaults.text_box?.left ?? 0, width: val, height: defaults.text_box?.height ?? 0 })
                }
              }}
            />
            <Input
              placeholder="高度"
              type="number"
              value={defaults.text_box?.height ?? ''}
              onChange={e => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                if (val !== undefined) {
                  updateDefault('text_box', { top: defaults.text_box?.top ?? 0, left: defaults.text_box?.left ?? 0, width: defaults.text_box?.width ?? 0, height: val })
                }
              }}
            />
          </div>
        </div>

        {/* Font color */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">字体颜色</label>
          <FontColorInput
            value={defaults.font_color}
            onChange={v => updateDefault('font_color', v as ItemRenderMetadata['font_color'])}
          />
        </div>

        {/* Text effect button group */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">文字效果</label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {(['none', 'outline', 'shadow'] as const).map(effect => (
              <button
                key={effect}
                type="button"
                className={clsx(
                  'px-4 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                  (defaults.text_effect ?? 'none') === effect
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                )}
                onClick={() => {
                  const updated = { ...defaults, text_effect: effect === 'none' ? undefined : effect }
                  if (effect === 'none') {
                    updated.outline_color = undefined
                    updated.shadow_blur = undefined
                    updated.shadow_opacity = undefined
                    updated.shadow_color = undefined
                    updated.shadow_distance = undefined
                    updated.shadow_angle = undefined
                    updated.shadow_spread = undefined
                    updated.shadow_blend_mode = undefined
                  } else if (effect === 'outline') {
                    updated.shadow_blur = undefined
                    updated.shadow_opacity = undefined
                    updated.shadow_color = undefined
                    updated.shadow_distance = undefined
                    updated.shadow_angle = undefined
                    updated.shadow_spread = undefined
                    updated.shadow_blend_mode = undefined
                  } else {
                    updated.outline_color = undefined
                  }
                  onChange({ ...metadata, default_metadata: updated })
                }}
              >
                {{ none: '无效果', outline: '描边', shadow: '投影' }[effect]}
              </button>
            ))}
          </div>
        </div>

        {/* Server-rendered text effect preview */}
        {defaults.text_effect && defaults.text_effect !== 'none' && (
          <ServerRenderedPreview packId={packId} meta={defaults} items={items} />
        )}

        {/* Outline fields */}
        {(defaults.text_effect === 'outline') && (
          <div className="pl-3 border-l-2 border-blue-200 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">描边颜色</label>
              <ColorInput
                placeholder='#FFFFFF'
                value={defaults.outline_color}
                onChange={v => updateDefault('outline_color', v)}
              />
            </div>
          </div>
        )}

        {/* Shadow fields */}
        {(defaults.text_effect === 'shadow') && (
          <div className="pl-3 border-l-2 border-blue-200 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">投影颜色</label>
                <ColorInput
                  placeholder='#444444'
                  value={defaults.shadow_color}
                  onChange={v => updateDefault('shadow_color', v)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">大小 (px)</label>
                <Input
                  type="number" step="1" placeholder="84"
                  value={defaults.shadow_blur ?? ''}
                  onChange={e => updateDefault('shadow_blur', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">不透明度 (%)</label>
                <Input
                  type="number" step="5" min="0" max="100" placeholder="85"
                  value={defaults.shadow_opacity ?? ''}
                  onChange={e => updateDefault('shadow_opacity', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">距离 (px)</label>
                <Input
                  type="number" step="1" min="0" placeholder="0"
                  value={defaults.shadow_distance ?? ''}
                  onChange={e => updateDefault('shadow_distance', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">角度 (°)</label>
                <Input
                  type="number" step="1" min="0" max="360" placeholder="120"
                  value={defaults.shadow_angle ?? ''}
                  onChange={e => updateDefault('shadow_angle', e.target.value ? parseInt(e.target.value) : undefined)}
                  disabled={!defaults.shadow_distance}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">扩展 (%)</label>
                <Input
                  type="number" step="5" min="0" max="100" placeholder="0"
                  value={defaults.shadow_spread ?? ''}
                  onChange={e => updateDefault('shadow_spread', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">混合模式</label>
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                {(['normal', 'multiply'] as const).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    className={clsx(
                      'px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-300',
                      (defaults.shadow_blend_mode ?? 'normal') === mode
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    )}
                    onClick={() => updateDefault('shadow_blend_mode', mode === 'normal' ? undefined : mode)}
                  >
                    {{ normal: '正常', multiply: '正片叠底' }[mode]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Numeric */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">旋转角度</label>
            <Input
              type="number" step="1" placeholder="0"
              value={defaults.rotation_angle ?? ''}
              onChange={e => updateDefault('rotation_angle', e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">字体缩放 X</label>
            <Input
              type="number" step="0.1" placeholder="1.0"
              value={defaults.font_scale_x ?? ''}
              onChange={e => updateDefault('font_scale_x', e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">行间距</label>
            <Input
              type="number" step="0.1" placeholder="1.3"
              value={defaults.line_spacing ?? ''}
              onChange={e => updateDefault('line_spacing', e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">文字对齐</label>
            <Select
              value={defaults.text_align ?? ''}
              onChange={e => updateDefault('text_align', (e.target.value || undefined) as 'left' | 'center' | 'right' | undefined)}
              options={[
                { value: '', label: '默认 (居中)' },
                { value: 'left', label: '左对齐' },
                { value: 'center', label: '居中' },
                { value: 'right', label: '右对齐' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Raw JSON toggle */}
      <div className="pt-3 border-t border-gray-200">
        <button
          type="button"
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          onClick={() => {
            if (!showRawJson) setRawText(JSON.stringify(metadata, null, 2))
            setShowRawJson(!showRawJson)
          }}
        >
          <Settings2 className="w-3 h-3" />
          {showRawJson ? '收起 Raw JSON' : '查看 Raw JSON'}
        </button>
        {showRawJson && (
          <Textarea
            className="font-mono text-xs mt-2"
            rows={6}
            value={rawText}
            onChange={e => {
              setRawText(e.target.value)
              try {
                const parsed = JSON.parse(e.target.value) as PackMetadata
                onChange(parsed)
              } catch {
                // ignore invalid JSON while typing
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Pack Detail (expanded inline)
// ============================================================================

function PackDetail({ packId, onClose }: { packId: string; onClose: () => void }) {
  const { data: pack, isLoading } = useContentPack(packId)
  const updateMutation = useUpdateContentPack()
  const deleteMutation = useDeleteContentPack()
  const uploadMutation = useUploadItems()
  const deleteItemMutation = useDeleteItem()

  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<ContentPackUpdate>({})
  const [packMetadata, setPackMetadata] = useState<PackMetadata>({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ContentPackItem | null>(null)

  // Initialize edit form when pack loads
  const startEditing = useCallback(() => {
    if (!pack) return
    setEditForm({
      display_name: pack.display_name,
      description: pack.description,
      status: pack.status,
      sort_order: pack.sort_order,
      is_default: pack.is_default,
    })
    setPackMetadata(pack.metadata || {})
    setEditing(true)
  }, [pack])

  const handleSave = async () => {
    if (!pack) return
    await updateMutation.mutateAsync({
      id: pack.id,
      data: { ...editForm, metadata: packMetadata },
    })
    setEditing(false)
  }

  const handleDelete = async () => {
    if (!pack) return
    await deleteMutation.mutateAsync(pack.id)
    onClose()
  }

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!pack) return
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return
    await uploadMutation.mutateAsync({ packId: pack.id, files: fileArray })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (!pack) return
    await deleteItemMutation.mutateAsync({ packId: pack.id, itemId })
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        加载中...
      </div>
    )
  }

  if (!pack) {
    return (
      <div className="p-6 text-center text-gray-500">
        内容包未找到
      </div>
    )
  }

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{pack.display_name}</h3>
          <StatusBadge status={pack.status} />
          {pack.is_default && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              默认
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Edit className="w-4 h-4 mr-1" />
              编辑
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>取消</Button>
              <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
                <Save className="w-4 h-4 mr-1" />
                保存
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <Card className="space-y-4">
          <Input
            label="显示名称"
            value={editForm.display_name || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
          />
          <Textarea
            label="描述"
            value={editForm.description || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
          />
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="状态"
              value={editForm.status || 'draft'}
              onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'active', label: '激活' },
                { value: 'archived', label: '归档' },
              ]}
            />
            <Input
              label="排序"
              type="number"
              value={editForm.sort_order ?? 0}
              onChange={(e) => setEditForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.is_default || false}
                  onChange={(e) => setEditForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">设为默认</span>
              </label>
            </div>
          </div>
          <PackMetadataEditor
            metadata={packMetadata}
            onChange={setPackMetadata}
            packId={pack.id}
            items={pack.items}
          />
        </Card>
      )}

      {/* Info (non-editing) */}
      {!editing && pack.description && (
        <p className="text-sm text-gray-600">{pack.description}</p>
      )}

      {/* Image Grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <Image className="w-4 h-4" />
            文件列表 ({pack.items?.length || 0})
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            loading={uploadMutation.isPending}
          >
            <Upload className="w-4 h-4 mr-1" />
            上传文件
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                handleFileUpload(e.target.files)
                e.target.value = ''
              }
            }}
          />
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={clsx(
            'border-2 border-dashed rounded-lg p-4 transition-colors',
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300',
            (!pack.items || pack.items.length === 0) && 'py-12'
          )}
        >
          {(!pack.items || pack.items.length === 0) ? (
            <div className="text-center text-gray-500">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">拖拽文件到此处或点击上传按钮</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {pack.items.map((item: ContentPackItem) => (
                <div
                  key={item.id}
                  className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                  onClick={() => setSelectedItem(item)}
                >
                  {/* Metadata indicator */}
                  {hasCustomMetadata(item.metadata) && (
                    <span className="absolute top-1 left-1 z-10 w-2 h-2 rounded-full bg-green-500" title="自定义配置" />
                  )}
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  {/* File info */}
                  <div className="p-2">
                    <p className="text-xs text-gray-700 truncate" title={item.filename}>
                      {item.filename}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(item.file_size)}
                    </p>
                  </div>
                  {/* Delete overlay */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id) }}
                    disabled={deleteItemMutation.isPending}
                    className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title="删除"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Pack */}
      <div className="border-t border-gray-200 pt-4">
        {!confirmDelete ? (
          <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4 mr-1" />
            删除内容包
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-red-600">确定要删除此内容包及所有文件吗？此操作不可撤销。</span>
            <Button variant="danger" size="sm" onClick={handleDelete} loading={deleteMutation.isPending}>
              确认删除
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
              取消
            </Button>
          </div>
        )}
      </div>

      {/* Item Metadata Dialog */}
      {selectedItem && (
        <ItemMetadataDialog
          open={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          item={selectedItem}
          packId={pack.id}
          items={pack.items}
        />
      )}
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

// ============================================================================
// Main Page
// ============================================================================

export function ContentPacksPage() {
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedPackId, setExpandedPackId] = useState<string | null>(null)
  const migrateMutation = useMigrateFilesystem()

  const queryType = typeFilter === 'all' ? undefined : typeFilter
  const queryStatus = statusFilter || undefined
  const { data, isLoading, error } = useContentPackList(queryType, queryStatus)

  const packs = data?.packs || []

  const toggleExpand = (packId: string) => {
    setExpandedPackId(prev => prev === packId ? null : packId)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-gray-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">内容包管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">管理签文主题、背景图等内容资源</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => migrateMutation.mutate()}
            loading={migrateMutation.isPending}
            title="从文件系统迁移到 OSS（仅需执行一次）"
          >
            <FolderInput className="w-4 h-4 mr-1" />
            迁移文件
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            创建内容包
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList>
            <TabsTrigger value="all">全部</TabsTrigger>
            <TabsTrigger value="lucky_charm_theme">签文主题</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: '所有状态' },
            { value: 'draft', label: '草稿' },
            { value: 'active', label: '已激活' },
            { value: 'archived', label: '已归档' },
          ]}
          className="w-32"
        />
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">加载中...</div>
      )}

      {error && (
        <div className="text-center py-12 text-red-500">
          加载失败: {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && packs.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>暂无内容包</p>
          <p className="text-sm mt-1">点击"创建内容包"按钮开始</p>
        </div>
      )}

      {!isLoading && !error && packs.length > 0 && (
        <div className="space-y-3">
          {packs.map((pack: ContentPack) => (
            <div key={pack.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Pack Card */}
              <button
                onClick={() => toggleExpand(pack.id)}
                className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Package className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{pack.display_name}</span>
                        <TypeBadge type={pack.type} />
                        <StatusBadge status={pack.status} />
                        {pack.is_default && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            默认
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pack.name} | {pack.item_count} 个文件 | 创建于 {formatDate(pack.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {expandedPackId === pack.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded Detail */}
              {expandedPackId === pack.id && (
                <PackDetail
                  packId={pack.id}
                  onClose={() => setExpandedPackId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total count */}
      {data && data.total > 0 && (
        <div className="text-sm text-gray-500 text-right">
          共 {data.total} 个内容包
        </div>
      )}

      {/* Create Dialog */}
      <CreatePackDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
